import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { IoCloudUpload, IoCloseCircle, IoAddCircle } from 'react-icons/io5';
import { FiClock, FiDownload, FiUser, FiActivity, FiCheckSquare, FiUploadCloud} from 'react-icons/fi';
import { ArrowDownTrayIcon } from '@heroicons/react/24/solid';
import api from '../../utils/api';
import Select from 'react-select';
import { toast } from 'react-toastify';

const priorityOptions = [
    { value: 1, label: 'Low' },
    { value: 2, label: 'Medium' },
    { value: 3, label: 'High' },
    { value: 4, label: 'Critical' },
];

const statusOptionsData = [
    { value: 1, label: 'New' },
    { value: 2, label: 'Under Discussion' },
    { value: 3, label: 'Under Process and Design Confirmation' },
    { value: 4, label: 'Under UI/UX Design' },
    { value: 5, label: 'SME Review' },
    { value: 6, label: 'Pending Client Design Approval' },
    { value: 7, label: 'Design Approved' },
    { value: 8, label: 'Requirement Freezed and moved to development' },
    { value: 9, label: 'Pending Client Verification' },
    { value: 10, label: 'Client Approved' },
    { value: 11, label: 'Completed' },
    { value: 12, label: 'Closed' },
    { value: 13, label: 'Rework Required' },
    { value: 14, label: 'On Hold' },
    { value: 15, label: 'Rejected' },
];

export default function ProjectManagementForm({ onSuccess, taskId, onClose, initialData = {} }) {
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [attachments, setAttachments] = useState([]);
    const [vhOptions, setVhOptions] = useState([]);
    const [empOptions, setEmpOptions] = useState([]);
    const [custOptions, setCustOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [systemModuleOptions, setSystemModuleOptions] = useState([]);

    const [form, setForm] = useState({
        project_name: '',
        customer_name: '',
        cust_key: '',
        title: '',
        sop_ref: '',
        module_name: '',
        module_ids: '',
        priority_id: 2,
        vertical_head_id: '',
        assigned_employee_ids: [],
        status_id: 1,
        expected_start_date: new Date().toISOString().split('T')[0],
        expected_completion_date: '',
        actual_completion_date: '',
        estimated_hours: '',
        worked_hours: '',
        current_worked_hours: '',
        description: '',
        internal_notes: '',
        version_number: 'V1.0.0',
        status_comment: '',
        document_file: '',
        is_draft: 0,
        is_visible_to_client: 0
    });

    const [isParsingDoc, setIsParsingDoc] = useState(false);

    useEffect(() => {
        const fetchResources = async () => {
            try {
                const [vhRes, empRes, custRes, statRes, sysModRes] = await Promise.all([
                    api.get('/project-management/get-vertical-heads'),
                    api.get('/project-management/get-employees'),
                    api.get('/project-management/get-customers'),
                    api.get('/project-management/get-statuses'),
                    api.get('/project-management/get-system-modules')
                ]);
                if (vhRes.data?.status) {
                    setVhOptions(vhRes.data.data.map(u => ({
                        value: u.user_id,
                        label: `${u.first_name} ${u.last_name} (${u.designation_name})`
                    })));
                }
                if (empRes.data?.status) {
                    setEmpOptions(empRes.data.data.map(u => ({
                        value: u.user_id,
                        label: `${u.first_name} ${u.last_name} (${u.designation_name})`
                    })));
                }
                if (custRes.data?.status) setCustOptions(custRes.data.data);
                if (statRes.data?.status) {
                    setStatusOptions(statRes.data.data.map(o => ({ value: Number(o.id), label: o.status_name })));
                }
                if (sysModRes.data?.status) setSystemModuleOptions(sysModRes.data.data);
            } catch (err) {
                console.error("Failed to fetch resources", err);
            }
        };
        fetchResources();
    }, []);

    useEffect(() => {
        if (initialData && (taskId || initialData.id)) {
            // Determine assignees based on whether it's a draft or a live task result
            const assigneeSource = initialData.assignee_ids || initialData.assigned_user_ids || '';
            const assignedIds = typeof assigneeSource === 'string' 
                ? assigneeSource.split(',').filter(x => x).map(Number) 
                : (Array.isArray(assigneeSource) ? assigneeSource : []);

            setForm({
                ...form,
                ...initialData,
                assigned_employee_ids: assignedIds
            });
        }
    }, [taskId, initialData]);

    const handleDocUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        // Save the file for final form submission
        setForm(prev => ({ ...prev, document_file: file }));

        const formData = new FormData();
        formData.append('doc', file);

        setIsParsingDoc(true);
        const toastId = toast.loading("Reading requirement document...");
        try {
            const res = await api.post('/project-management/read-doc', formData);
            if (res.data?.status) {
                const content = res.data.content;
                
                // Convert structured content to HTML
                let htmlContent = "";
                if (Array.isArray(content)) {
                    htmlContent = content.map(item => {
                        if (item.type === 'text') {
                            // Smart Text Formatting (Titles, Bullets, Spacing)
                            const lines = item.value.split('\n');
                            return lines.map(line => {
                                const trimmed = line.trim();
                                if (!trimmed) return "<p><br/></p>";
                                // Detected Section Header (Ends with :, or fits title pattern)
                                if (trimmed.endsWith(':') || /^\d+\./.test(trimmed) || (trimmed.length < 30 && /^[A-Z][A-Za-z\s]+$/.test(trimmed))) {
                                    return `<h3 style="margin-top: 15px; color: #1e293b; font-weight: 800; border-bottom: 2px solid #f1f5f9; padding-bottom: 5px;">${trimmed}</h3>`;
                                }
                                // Detected Bullet (- or *)
                                if (/^[•\-\*]/.test(trimmed)) {
                                    return `<li style="margin-left: 20px; margin-bottom: 5px; list-style-type: disc;">${trimmed.replace(/^[•\-\*]\s*/, '')}</li>`;
                                }
                                return `<p style="margin-bottom: 8px; line-height: 1.6;">${trimmed}</p>`;
                            }).join('');
                        } else if (item.type === 'table') {
                            // High Fidelity Flex Table
                            const tableData = Array.isArray(item.value) ? item.value : null;
                            if (tableData) {
                                const tableHtml = tableData.map((row, rIdx) => {
                                    const colWidth = row.length > 0 ? (100 / row.length).toFixed(2) : 100;
                                    return `<div style="display: block; border-bottom: 1px solid #e2e8f0; white-space: nowrap; ${rIdx === 0 ? 'background: #f8fafc; font-weight: bold; color: #334155;' : ''}">
                                        ${row.map(cell => `<span style="display: inline-block; width: ${colWidth}%; padding: 12px; border-right: 1px solid #e2e8f0; box-sizing: border-box; vertical-align: top; white-space: normal; font-size: 13px;">${cell}</span>`).join('')}
                                    </div>`;
                                }).join('');
                                return `<div style="border: 1px solid #e2e8f0; border-bottom: none; border-right: none; border-radius: 8px; overflow: hidden; margin: 25px 0; background: #fff; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);">${tableHtml}</div><p></p>`;
                            } else if (typeof item.value === 'string' && item.value.includes('<table')) {
                                return `<div style="overflow-x: auto; margin: 25px 0; border: 1px solid #e2e8f0; border-radius: 8px;">${item.value}</div><p></p>`;
                            }
                        } else if (item.type === 'image') {
                            return `<div style="margin: 25px 0; border-radius: 12px; overflow: hidden; border: 1px solid #f1f5f9; box-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1); max-width: 100%; text-align: center; background: #fff; padding: 10px;">
                                <img src="${item.value}" style="max-width: 100%; height: auto; border-radius: 8px;" alt="Extracted Image" />
                            </div><p></p>`;
                        } else if (item.type === 'list_item') {
                            return `<li style="margin-left: 20px; list-style-type: none; margin-bottom: 8px; line-height: 1.6; color: #334155;">${item.value}</li>`;
                        }
                        return "";
                    }).join('');
                } else {
                    htmlContent = String(content).replace(/\n/g, '<br/>');
                }

                // Replace existing description with new extraction
                setForm(prev => ({ 
                    ...prev, 
                    description: "<h3>Extracted from Document:</h3>" + htmlContent
                }));

                toast.update(toastId, { render: "Document parsed successfully! Text, tables, and images imported.", type: "success", isLoading: false, autoClose: 3000 });
            } else {
                toast.update(toastId, { render: "Failed to parse document: " + (res.data?.error || "Unknown error"), type: "error", isLoading: false, autoClose: 3000 });
            }
        } catch (err) {
            toast.update(toastId, { render: "Error reading document", type: "error", isLoading: false, autoClose: 3000 });
        } finally {
            setIsParsingDoc(false);
            e.target.value = '';
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleQuillChange = (name, content) => {
        setForm(prev => ({ ...prev, [name]: content }));
    };

    const handleFileChange = (e) => {
        const newFiles = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...newFiles]);
        e.target.value = ''; // Reset input to allow re-choosing same files
    };

    const removeFile = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.project_name) newErrors.project_name = 'Project Name is required';
        if (!form.customer_name) newErrors.customer_name = 'Customer Name is required';
        if (!form.module_ids) newErrors.module_ids = 'System Module is required';
        if (!form.title) newErrors.title = 'Requirement Title is required';
        if (!form.vertical_head_id) newErrors.vertical_head_id = 'Vertical Head is required';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        const formData = new FormData();
        Object.keys(form).forEach(key => {
            if (key !== 'assigned_employee_ids' && form[key] !== null) {
                formData.append(key, form[key]);
            }
        });

        // Add multi-assignees
        if (form.assigned_employee_ids.length > 0) {
            formData.append('assigned_user_ids', form.assigned_employee_ids.join(','));
        }

        attachments.forEach(file => formData.append('files[]', file));

        try {
            const endpoint = taskId ? `/project-management/${taskId}` : '/project-management';
            const res = await api.post(endpoint, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                // If it was a draft being submitted, delete the draft source
                if (initialData?.is_draft_item && initialData?.id) {
                    await api.post(`/project-management/delete-draft/${initialData.id}`);
                }
                onSuccess?.();
                onClose?.();
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleSaveDraft = async (e) => {
        e.preventDefault();
        // Validation for draft: only Title is mandatory to save a workspace
        if (!form.title) {
            setErrors({ title: 'Requirement Title is required to save a draft' });
            return;
        }

        setSubmitting(true);
        const formData = new FormData();
        Object.keys(form).forEach(key => {
            if (key !== 'assigned_employee_ids' && form[key] !== null) {
                formData.append(key, form[key]);
            }
        });

        if (form.assigned_employee_ids.length > 0) {
            formData.append('assigned_user_ids', form.assigned_employee_ids.join(','));
        }

        attachments.forEach(file => formData.append('files[]', file));

        try {
            const res = await api.post('/project-management/save-draft', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.status) {
                onSuccess?.(true);
                onClose?.();
            }
        } catch (err) {
            console.error('Draft save failed', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="relative">
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[80vh] overflow-y-auto bg-white rounded-xl shadow-inner border border-gray-100 custom-scrollbar">

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* Basic Info */}
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Project Name *</label>
                    <input autoFocus name="project_name" value={form.project_name} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main focus:border-main outline-none transition-all shadow-sm" />
                    {errors.project_name && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.project_name}</p>}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Customer Name *</label>
                    <Select
                        options={custOptions}
                        value={custOptions.find(o => o.label === form.customer_name)}
                        onChange={opt => setForm(prev => ({ ...prev, customer_name: opt.label }))}
                        placeholder="Select Customer..."
                        className="text-sm shadow-sm"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                    {errors.customer_name && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.customer_name}</p>}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Modules (Multi-Select) *</label>
                    <Select
                        isMulti
                        options={systemModuleOptions}
                        value={systemModuleOptions.filter(o => form.module_ids?.split(',').includes(String(o.value)))}
                        onChange={opts => setForm(prev => ({ ...prev, module_ids: opts ? opts.map(o => o.value).join(',') : '' }))}
                        placeholder="Select System Modules..."
                        className="text-sm shadow-sm"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                    {errors.module_ids && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.module_ids}</p>}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Requirement Title *</label>
                    <input name="title" value={form.title} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main focus:border-main outline-none transition-all shadow-sm" />
                    {errors.title && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.title}</p>}
                </div>

                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Priority *</label>
                    <Select
                        options={priorityOptions}
                        value={priorityOptions.find(o => o.value === form.priority_id)}
                        onChange={opt => setForm(prev => ({ ...prev, priority_id: opt.value }))}
                        className="text-sm shadow-sm"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                </div>

                {/* Assignment */}
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Vertical Head *</label>
                    <Select
                        options={vhOptions}
                        value={vhOptions.find(o => String(o.value) === String(form.vertical_head_id))}
                        onChange={opt => setForm(prev => ({ ...prev, vertical_head_id: opt.value }))}
                        className="text-sm shadow-sm"
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                    {errors.vertical_head_id && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.vertical_head_id}</p>}
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Assigned To * (Multi-Select)</label>
                    <Select
                        isMulti
                        options={empOptions}
                        value={empOptions.filter(o => form.assigned_employee_ids.includes(Number(o.value)))}
                        onChange={opts => setForm(prev => ({ ...prev, assigned_employee_ids: opts ? opts.map(o => Number(o.value)) : [] }))}
                        className="text-sm shadow-sm"
                        placeholder="Select Team Members..."
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Status *</label>
                    <Select
                        options={statusOptions}
                        value={statusOptions.find(o => o.value === form.status_id)}
                        onChange={opt => setForm(prev => ({ ...prev, status_id: opt.value }))}
                        className="text-sm shadow-sm"
                        isDisabled={!taskId}
                        menuPortalTarget={document.body}
                        styles={{ menuPortal: base => ({ ...base, zIndex: 999999 }) }}
                    />
                </div>

                {/* Dates & Hours */}
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Exp. Start Date</label>
                    <input type="date" name="expected_start_date" value={form.expected_start_date} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none shadow-sm" />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Exp. Comp. Date</label>
                    <input type="date" name="expected_completion_date" value={form.expected_completion_date} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none shadow-sm" />
                </div>
                {/* <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Est. Hours</label>
                    <input type="number" name="estimated_hours" value={form.estimated_hours} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none shadow-sm" />
                </div> */}

                {taskId && (
                    <>
                        {/* <div className="space-y-1">
                            <label className="text-xs font-bold text-main uppercase flex items-center gap-1">
                                <FiClock className="text-[10px]" /> Add Worked Hours
                            </label>
                            <input 
                                type="number" 
                                name="current_worked_hours" 
                                value={form.current_worked_hours} 
                                onChange={handleChange} 
                                step="0.01"
                                placeholder="Hours spent..." 
                                className="w-full border-2 border-main/20 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none shadow-sm bg-main/5 font-bold text-main placeholder:font-normal placeholder:text-gray-300" 
                            />
                        </div> */}
                        <div className="space-y-1 md:col-span-2 lg:col-span-3">
                            <label className="text-xs font-bold text-gray-500 uppercase uppercase">Status Change Comment / Work Done</label>
                            <input 
                                name="status_comment" 
                                value={form.status_comment} 
                                onChange={handleChange} 
                                placeholder="Briefly describe the work done or reason for status change..." 
                                className="w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-main outline-none shadow-sm" 
                            />
                        </div>
                    </>
                )}
            </div>

            {/* Ref Fields & Upload in a single row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end bg-slate-50/50 p-4 rounded-2xl border border-gray-100 shadow-inner">
                <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">SOP/Requirement Ref</label>
                    <input 
                        name="sop_ref"
                        value={form.sop_ref}
                        onChange={handleChange}
                        placeholder="Type SOP Code..."
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20 shadow-sm transition-all"
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] ml-1">Module Ref</label>
                    <input 
                        name="module_name"
                        value={form.module_name}
                        onChange={handleChange}
                        placeholder="Type Module Ref(MOD101)..."
                        className="w-full bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-sm font-bold text-gray-700 outline-none focus:ring-2 focus:ring-main/20 shadow-sm transition-all"
                    />
                    {errors.module_name && <p className="text-red-500 text-[10px] mt-1 font-medium">{errors.module_name}</p>}
                </div>
                <div className="flex items-center">
                    <label className={`flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] cursor-pointer transition-all border shadow-md h-[42px] ${isParsingDoc ? 'bg-gray-100 text-gray-400 border-gray-200' : form.document_file ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-main text-white border-main hover:bg-main-dark active:scale-95 hover:shadow-lg hover:shadow-main/20'}`}>
                        {isParsingDoc ? (
                            <>
                                <div className="w-3 h-3 border-2 border-gray-300 border-t-main rounded-full animate-spin" />
                                Parsing Doc...
                            </>
                        ) : form.document_file ? (
                            <>
                                <FiCheckSquare className="text-sm" />
                                Doc Read (Change?)
                            </>
                        ) : (
                            <>
                                <FiUploadCloud className="text-sm" />
                                Requirement Doc (Auto Read)
                            </>
                        )}
                        <input type="file" accept=".doc,.docx,.pdf" onChange={handleDocUpload} className="hidden" disabled={isParsingDoc} />
                    </label>
                </div>
            </div>

            {/* Rich Text Areas */}
            <div className="space-y-4 pt-2">
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase px-1">Requirement Description </label> <span className="text-xs font-bold text-red-500 px-1">(Kindly Copy and Paste the table content as image or ScreenShot)</span>
                    <ReactQuill theme="snow" value={form.description} onChange={content => handleQuillChange('description', content)} className="custom-quill bg-white rounded-2xl border shadow-sm mb-2" />
                </div>
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase px-1">Internal Notes</label>
                    <ReactQuill theme="snow" value={form.internal_notes} onChange={content => handleQuillChange('internal_notes', content)} className="custom-quill bg-white rounded-lg border shadow-sm" />
                </div>
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                    <label className="text-xs font-bold text-gray-500 uppercase">Version / Revision</label>
                    <input name="version_number" value={form.version_number} onChange={handleChange} className="w-full border rounded-lg px-3 py-2 text-sm shadow-sm" />
                </div>
                <div className="space-y-2 col-span-2">
                    <label className="text-xs font-bold text-gray-500 uppercase px-1">Attachments</label>
                    <div className="space-y-3">
                        <div className="flex flex-col space-y-1 px-1">
                            <span className="text-[10px] text-gray-400 normal-case font-normal italic tracking-wide">
                                Max 5MB per file. Allowed formats: PDF, DOC, DOCX, XLS, XLSX, ZIP, JPG, PNG
                            </span>
                        </div>
                        
                        <div className="flex flex-wrap gap-4 items-center">
                            <label className="flex items-center gap-3 px-6 py-2.5 bg-red-600 text-white rounded-xl text-xs font-black uppercase tracking-widest cursor-pointer hover:bg-red-700 transition-all shadow-lg shadow-red-100 hover:shadow-red-200 active:scale-95">
                                <IoCloudUpload className="text-lg" />
                                Choose Files
                                <input type="file" multiple onChange={handleFileChange} className="hidden" />
                            </label>
                            
                            <div className="flex flex-wrap gap-3">
                                {attachments.map((file, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-white border border-gray-200 px-3 py-2 rounded-xl text-xs text-gray-700 group hover:border-red-200 hover:bg-red-50 transition-all shadow-sm">
                                        <span className="truncate max-w-[200px] font-bold">{file.name}</span>
                                        <button type="button" onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500 transition-colors">
                                            <IoCloseCircle className="text-base" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-gray-100">
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-4 p-2 bg-slate-50/50 rounded-2xl border border-slate-100 w-full sm:w-auto">
                    <button 
                        type="button"
                        onClick={handleSaveDraft}
                        disabled={submitting}
                        className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-100 transition-all disabled:opacity-50"
                    >
                        <FiCheckSquare className="text-sm" />
                        Save as Draft
                    </button>

                    <div className="hidden sm:block w-px h-4 bg-gray-200" />

                    <label className="relative inline-flex items-center cursor-pointer group">
                        <input 
                            type="checkbox" 
                            className="sr-only peer" 
                            checked={form.is_visible_to_client == 1}
                            disabled={form.is_draft == 1}
                            onChange={e => setForm(prev => ({ ...prev, is_visible_to_client: e.target.checked ? 1 : 0 }))}
                        />
                        <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500 peer-disabled:opacity-40"></div>
                        <span className={`ms-2 text-[9px] font-black uppercase tracking-widest transition-colors ${form.is_draft == 1 ? 'text-gray-300' : 'text-gray-500 group-hover:text-emerald-600'}`}>Visible to Client</span>
                    </label>
                </div>
                <div className="flex gap-3 w-full sm:w-auto">
                    <button type="button" onClick={onClose} className="flex-1 sm:flex-none px-6 py-2 border rounded-full text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all uppercase tracking-wider">Cancel</button>
                    <button type="submit" disabled={submitting} className="flex-1 sm:flex-none px-8 py-2 bg-main text-white rounded-full text-sm font-bold hover:bg-main-dark transition-all disabled:opacity-50 uppercase tracking-widest shadow-lg shadow-main/20">
                        {submitting ? 'Saving...' : (initialData?.is_draft_item ? 'Publish' : (taskId ? 'Update' : 'Create'))}
                    </button>
                </div>  
            </div>
        </form>
    </div>
    );
}
